/**
 * React Query hooks for project data management
 * Replaces Zustand store with server state management
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/services/api/client';
import { AxiosError } from 'axios';

// Types
interface ProjectData {
  consumption?: any;
  bill?: any;
  prices?: any;
  aligned_consumption?: any;
  aligned_prices?: any;
}

interface Project {
  id: string;
  name: string;
  description: string;
  status: string;
  selected_barra?: string;
  solar_enabled?: boolean | null;
}

interface ProjectResponse {
  project: Project;
  data: ProjectData;
}

/**
 * Hook to fetch all data for a specific project
 * @param projectId - The project ID
 * @param useAligned - Whether to use aligned data (default: true)
 */
export function useProjectData(projectId: string | undefined, useAligned: boolean = true) {
  return useQuery<ProjectData>({
    queryKey: ['project', projectId, 'data', useAligned],
    queryFn: async () => {
      if (!projectId) throw new Error('No project ID provided');

      const response = await apiClient.get(`/projects/${projectId}/data`, {
        params: { use_aligned: useAligned }
      });
      return response.data.data;
    },
    enabled: !!projectId,
    staleTime: 0, // Always consider data stale to ensure fresh data after mutations
    cacheTime: 5 * 60 * 1000, // Keep in cache for 5 minutes
    refetchOnWindowFocus: false, // Don't refetch when window regains focus
  });
}

/**
 * Hook to fetch list of all projects
 */
export function useProjects() {
  return useQuery({
    queryKey: ['projects'],
    queryFn: async () => {
      // Request all projects (max 100) to show complete list in dropdown
      const response = await apiClient.get('/projects', {
        params: { per_page: 100 }
      });
      return response.data.projects;
    },
  });
}

/**
 * Hook to get current project
 */
export function useCurrentProject() {
  return useQuery<Project | null>({
    queryKey: ['current-project'],
    queryFn: async () => {
      const response = await apiClient.get('/projects/current');
      return response.data.project ?? null;
    },
    staleTime: 0, // Always consider data stale to ensure fresh data after mutations
    cacheTime: 5 * 60 * 1000,
  });
}

/**
 * Hook to upload data (consumption, price, or bill) with async progress tracking
 * Supports both single file and multiple files
 */
export function useUploadData(
  projectId: string | undefined,
  onProgress?: (progress: number, step: string) => void
) {
  const queryClient = useQueryClient();

  return useMutation<any, AxiosError, { file?: File; files?: File[]; type: string; billType?: string }>({
    mutationFn: async ({ file, files, type, billType }) => {
      if (!projectId) throw new Error('No project ID provided');

      const formData = new FormData();

      // Support both single file and multiple files
      if (files && files.length > 0) {
        // Multiple files - append each with 'files' key
        files.forEach(f => {
          formData.append('files', f);
        });
      } else if (file) {
        // Single file - use 'file' key for backwards compatibility
        formData.append('file', file);
      } else {
        throw new Error('No file(s) provided');
      }

      formData.append('type', type);

      // Add bill_type if provided (for bill uploads)
      if (billType) {
        formData.append('bill_type', billType);
      }

      // Start async upload - returns immediately with job_id
      const startResponse = await apiClient.post(
        `/projects/${projectId}/upload/async`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      const { job_id } = startResponse.data;

      // Poll for progress
      return await pollUploadProgress(projectId, job_id, onProgress);
    },
    onSuccess: () => {
      // Immediately invalidate and refetch project data cache
      // This ensures that when user navigates back to Step 2, they see fresh data
      queryClient.invalidateQueries({
        queryKey: ['project', projectId, 'data'],
      });
      // Projects list doesn't need immediate refetch
      queryClient.invalidateQueries({
        queryKey: ['projects'],
        refetchType: 'none',
      });
    },
  });
}

/**
 * Poll for upload progress until completion
 */
async function pollUploadProgress(
  projectId: string,
  jobId: string,
  onProgress?: (progress: number, step: string) => void
): Promise<any> {
  const pollInterval = 1000; // 1 second
  const maxAttempts = 300; // 5 minutes max
  let attempts = 0;

  while (attempts < maxAttempts) {
    await new Promise(resolve => setTimeout(resolve, pollInterval));
    attempts++;

    const response = await apiClient.get(
      `/projects/${projectId}/upload/${jobId}/progress`
    );

    const { job } = response.data;

    // Report progress
    if (onProgress) {
      onProgress(job.progress, job.current_step || 'Processing...');
    }

    // Check completion
    if (job.status === 'completed') {
      return job.result;
    }

    if (job.status === 'failed') {
      throw new Error(job.error || 'Upload failed');
    }

    if (job.status === 'cancelled') {
      throw new Error('Upload was cancelled');
    }
  }

  throw new Error('Upload timeout - processing took too long');
}

/**
 * Hook to load a project
 */
export function useLoadProject() {
  const queryClient = useQueryClient();

  return useMutation<ProjectResponse, AxiosError, string>({
    mutationFn: async (projectId: string) => {
      const response = await apiClient.post(`/projects/${projectId}/load`);
      return response.data;
    },
    onSuccess: (data) => {
      // Update the cache with loaded project data
      queryClient.setQueryData(['project', data.project.id, 'data'], data.data);
      queryClient.setQueryData(['current-project'], data.project);
      // Only invalidate projects list, don't refetch anything automatically
      queryClient.invalidateQueries({
        queryKey: ['projects'],
        refetchType: 'none', // Mark as stale but don't trigger automatic refetch
      });
    },
  });
}

/**
 * Hook to create a new project
 */
export function useCreateProject() {
  const queryClient = useQueryClient();
  
  return useMutation<Project, AxiosError, { name: string; description?: string }>({
    mutationFn: async (projectData) => {
      const response = await apiClient.post('/projects', projectData);
      return response.data.project;
    },
    onSuccess: (newProject) => {
      // Update projects list
      queryClient.invalidateQueries(['projects']);
      // Set as current project
      queryClient.setQueryData(['current-project'], newProject);
    },
  });
}

/**
 * Hook to update a project
 */
export function useUpdateProject(projectId: string | undefined) {
  const queryClient = useQueryClient();
  
  return useMutation<Project, AxiosError, Partial<Project>>({
    mutationFn: async (updates) => {
      if (!projectId) throw new Error('No project ID provided');

      const response = await apiClient.put(`/projects/${projectId}`, updates);
      return response.data.project;
    },
    onSuccess: () => {
      // Only invalidate project data, not configuration
      queryClient.invalidateQueries(['project', projectId, 'data']);
      queryClient.invalidateQueries(['projects']);
    },
  });
}

/**
 * Hook to delete a project
 */
export function useDeleteProject() {
  const queryClient = useQueryClient();

  return useMutation<void, AxiosError, string>({
    mutationFn: async (projectId: string) => {
      await apiClient.delete(`/projects/${projectId}`);
    },
    onSuccess: async (_, deletedProjectId) => {
      // Clear cache entries for the deleted project to prevent 404 errors
      queryClient.removeQueries({ queryKey: ['project', deletedProjectId] });

      // Get the current project from cache
      const currentProject = queryClient.getQueryData<Project>(['current-project']);

      // If we deleted the current project, select another one
      if (currentProject?.id === deletedProjectId) {
        // Get the projects list from cache
        const projects = queryClient.getQueryData<Project[]>(['projects']) || [];

        // Find another project to select (first project that isn't the deleted one)
        const nextProject = projects.find(p => p.id !== deletedProjectId);

        if (nextProject) {
          // Load the next project
          try {
            const response = await apiClient.post(`/projects/${nextProject.id}/load`);
            queryClient.setQueryData(['project', nextProject.id, 'data'], response.data.data);
            queryClient.setQueryData(['current-project'], nextProject);
          } catch (error) {
            console.error('Failed to auto-load next project:', error);
            // Clear current project if loading fails
            queryClient.setQueryData(['current-project'], null);
          }
        } else {
          // No other projects available, clear current project
          queryClient.setQueryData(['current-project'], null);
        }
      }

      // Invalidate and refetch projects list
      queryClient.invalidateQueries(['projects']);
    },
  });
}

/**
 * Hook to duplicate a project (copies all data)
 */
export function useDuplicateProject() {
  const queryClient = useQueryClient();

  return useMutation<Project, AxiosError, { projectId: string; name?: string }>({
    mutationFn: async ({ projectId, name }) => {
      const response = await apiClient.post(`/projects/${projectId}/duplicate`, { name: name || null });
      return response.data.project;
    },
    onSuccess: (newProject) => {
      queryClient.invalidateQueries(['projects']);
      queryClient.setQueryData(['current-project'], newProject);
    },
  });
}

/**
 * Hook to delete consumption data for a project
 */
export function useDeleteConsumptionData(projectId: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation<any, AxiosError>({
    mutationFn: async () => {
      if (!projectId) throw new Error('No project ID provided');

      const response = await apiClient.delete(`/projects/${projectId}/consumption`);
      return response.data;
    },
    onSuccess: () => {
      // Remove all cached project data to force fresh fetch
      queryClient.removeQueries({ queryKey: ['project'] });
      queryClient.removeQueries({ queryKey: ['projects'] });
      queryClient.removeQueries({ queryKey: ['projectData'] });
      queryClient.removeQueries({ queryKey: ['projectCompleteness'] });
      queryClient.removeQueries({ queryKey: ['patternMetrics'] });
    },
  });
}

/**
 * Hook to delete price data for a project
 */
export function useDeletePriceData(projectId: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation<any, AxiosError>({
    mutationFn: async () => {
      if (!projectId) throw new Error('No project ID provided');

      const response = await apiClient.delete(`/projects/${projectId}/prices`);
      return response.data;
    },
    onSuccess: () => {
      // Remove all cached project data to force fresh fetch
      queryClient.removeQueries({ queryKey: ['project'] });
      queryClient.removeQueries({ queryKey: ['projects'] });
      queryClient.removeQueries({ queryKey: ['projectData'] });
      queryClient.removeQueries({ queryKey: ['projectCompleteness'] });
      queryClient.removeQueries({ queryKey: ['patternMetrics'] });
    },
  });
}

/**
 * Hook to get consumption data from project data
 */
export function useConsumptionData(projectId: string | undefined, useAligned: boolean = true) {
  const { data, ...rest } = useProjectData(projectId, useAligned);
  
  return {
    data: data?.consumption,
    ...rest,
  };
}

/**
 * Hook to get price data from project data
 */
export function usePriceData(projectId: string | undefined, useAligned: boolean = true) {
  const { data, ...rest } = useProjectData(projectId, useAligned);
  
  return {
    data: data?.prices,
    ...rest,
  };
}

/**
 * Hook to get bill data from project data
 */
export function useBillData(projectId: string | undefined) {
  const { data, ...rest } = useProjectData(projectId);

  return {
    data: data?.bill,
    ...rest,
  };
}

/**
 * Hook to delete bill data for a project
 */
export function useDeleteBill(projectId: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation<void, AxiosError>({
    mutationFn: async () => {
      if (!projectId) {
        throw new Error('Project ID is required');
      }
      await apiClient.delete(`/projects/${projectId}/bill`);
    },
    onSuccess: () => {
      if (projectId) {
        // Only invalidate project data, not configuration
        queryClient.invalidateQueries(['project', projectId, 'data']);
      }
    },
  });
}

/**
 * Hook to check if project has all required data
 */
export function useProjectCompleteness(projectId: string | undefined) {
  // Get original data to check what's uploaded
  const { data: originalData } = useProjectData(projectId, false);
  // Get aligned data to check if alignment is done
  const { data: alignedData } = useProjectData(projectId, true);
  // Get project for selected_barra
  const { data: project } = useCurrentProject();

  // Check if we have aligned data
  // Aligned data exists if we have both consumption and prices with records
  const hasAlignedConsumption = (alignedData?.consumption?.totalRecords || 0) > 0;
  const hasAlignedPrices = (alignedData?.prices?.records || 0) > 0;

  return {
    hasConsumption: !!originalData?.consumption,
    hasPrices: !!originalData?.prices,
    hasBill: !!originalData?.bill,
    hasSelectedBarra: !!project?.selected_barra,
    hasAllData: !!(originalData?.consumption && originalData?.prices && originalData?.bill),
    hasAlignedData: hasAlignedConsumption && hasAlignedPrices,
  };
}